import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import MUIAutocomplete from "@mui/material/Autocomplete";

import { Chip, CircularProgress, debounce } from "@mui/material";
import { darken, lighten, useTheme } from "@mui/material/styles";
import { VariableSizeList, ListChildComponentProps } from "react-window";

import FormField from "../FormField";
import TitleValue from "../TitleValue";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";

import { Mode } from "../../constants/enums";
import { ICodename } from "../../commons/interfaces";
import { AddCircleOutline, RestartAltOutlined } from "@mui/icons-material";

const ADD_NEW_CODE = "add_new";
const LOAD_MORE_CODE = "load_more";

interface IProps {
    size?: any;
    mode?: number;
    label?: string;
    placeholder?: string;
    variant?: "standard" | "outlined";

    loading?: boolean;
    required?: boolean;
    multiple?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    isFullRow?: boolean;
    isGroupBy?: boolean;
    isSelectedBox?: boolean;

    data: ICodename[];
    defaultValue?: any;
    errorMessage?: string;
    noOptionsText?: string;
    optionDisabled?: string[];
    onChange: (value: any) => void;

    //
    isAllowCreateNew?: boolean;
    createNewCallback?: () => void;

    //
    debounceTime?: number;
    valueSearchText?: string;
    isAllowLoadMore?: boolean;
    onCallLoadMoreData?: (searchText?: string) => void;
}

const Autocomplete = ({
    mode,
    data,
    size,
    label,
    variant,
    loading,
    required,
    multiple,
    disabled,
    readOnly,
    isFullRow,
    isGroupBy,
    isSelectedBox,

    placeholder,
    defaultValue,
    errorMessage,
    noOptionsText,
    optionDisabled,
    onChange,

    isAllowCreateNew,
    createNewCallback,

    isAllowLoadMore,
    valueSearchText,
    onCallLoadMoreData,
    debounceTime = 500,
}: IProps) => {
    const strings = useStrings();
    const [open, setOpen] = React.useState(false);

    const formatValue = (newValue: any) => {
        if (newData.length > 0) {
            if (multiple || Array.isArray(newValue)) {
                if ([...newValue || []].length > 0) {
                    const selectedOption: ICodename[] = [];
                    newValue.forEach((val: any) => {
                        const temp = newData.find((el) => `${el.code}` === `${val}`);
                        !Helpers.isNullOrEmpty(temp) && selectedOption.push(temp);
                    });
                    return selectedOption;
                }
                return [];
            } else {
                const selectedOption = newData.find((el) => `${el.code}` === `${newValue}`);
                return !Helpers.isNullOrEmpty(selectedOption) ? selectedOption : null;
            }
        } else {
            return multiple ? [] : null;
        }
    };

    const getPlaceholder = () => {
        if (multiple && Array.isArray(defaultValue)) {
            return defaultValue.length > 0 ? "" : placeholder;
        } else {
            return Helpers.isNullOrEmpty(defaultValue) ? placeholder : "";
        }
    };

    const handleOnChange = (selectedOption: any) => {
        if (multiple && Array.isArray(selectedOption)) {
            const oldOptions = selectedOption.filter((option) => option.code !== ADD_NEW_CODE);
            if (isAllowCreateNew && oldOptions.length < selectedOption.length) {
                Helpers.isFunction(createNewCallback) && createNewCallback();
                setOpen(false);
            } else if (
                isAllowLoadMore &&
                selectedOption?.[selectedOption.length - 1]?.code === LOAD_MORE_CODE
            ) {
                Helpers.isFunction(onCallLoadMoreData) && onCallLoadMoreData();
            } else {
                let result: string[] = [];
                for (let items of selectedOption) {
                    result.push(items.code);
                }
                onChange(result);
                setOpen(false);
            }
        } else {
            if (isAllowCreateNew && selectedOption?.code === ADD_NEW_CODE) {
                setOpen(false);
                Helpers.isFunction(createNewCallback) && createNewCallback();
            } else if (isAllowLoadMore && selectedOption?.code === LOAD_MORE_CODE) {
                Helpers.isFunction(onCallLoadMoreData) && onCallLoadMoreData();
            } else {
                setOpen(false);
                onChange(selectedOption ? selectedOption?.code : null);
            }
        }
    };

    const newData = useMemo(() => {
        let newData = [...data || []];

        if (isGroupBy) {
            newData = newData.sort((a, b) => -b.group?.localeCompare(a.group))
        }

        if (isAllowCreateNew) {
            newData = [{ code: ADD_NEW_CODE, name: ADD_NEW_CODE, }, ...newData || []];
        }

        if (isAllowLoadMore) {
            newData = [...newData || [], { code: LOAD_MORE_CODE, name: LOAD_MORE_CODE, }];
        }

        return newData;
    }, [isGroupBy, isAllowCreateNew, isAllowLoadMore, data]);

    const deboundGetDataByApi = useMemo(() => debounce(async (text?: string) => {
        try {
            onCallLoadMoreData &&
                await onCallLoadMoreData(text);
        } catch (error) { } finally { }
    }, debounceTime), [debounceTime]);

    if (mode === Mode.View) {
        let val: string[] = [];
        if (multiple) {
            [...(defaultValue || [])].forEach((str) => {
                const item = newData.find((e) => `${e.code}` === `${str}`);
                item && val.push(item?.name);
            });
        } else {
            const itiem = newData.find((el) => `${el.code}` === `${defaultValue}`)?.name;
            itiem && val.push(itiem);
        }
        return <TitleValue title={label} isFullRow={isFullRow} value={val.join(", ")} />;
    };

    return (
        <MUIAutocomplete
            freeSolo
            id={"auto_" + label}
            key={(defaultValue as any) || (newData as any)}
            isOptionEqualToValue={(option, value) => (Array.isArray(option) ? option[0] : option)?.code === (Array.isArray(value) ? value[0] : value)?.code}

            ListboxComponent={ListboxComponent}
            renderOption={(props, option, state) => ([props, (Array.isArray(option) ? option[0] : option)?.name, state.index] as React.ReactNode)}

            disableListWrap
            options={newData}
            multiple={multiple}
            disabled={disabled}
            readOnly={readOnly}
            disableClearable={isSelectedBox}
            value={formatValue(defaultValue)}

            open={open}
            loading={open && loading}
            onOpen={(event) => { setOpen(true); }}
            onClose={(event, reason) => { setOpen(false); }}

            noOptionsText={noOptionsText || strings.Common.NO_DATA}

            renderGroup={isGroupBy ? (params) => params as any : undefined}
            groupBy={isGroupBy ? (option) => ((Array.isArray(option)) ? option[0] : option)?.group : undefined}

            getOptionLabel={(option: any) => (((Array.isArray(option)) ? option[0] : option)?.name || "")}
            getOptionDisabled={(option: any) => [...(optionDisabled || [])].indexOf(`${option?.code}`) !== -1}
            renderTags={(tagValue: any[], getTagProps, ownerState) =>
                tagValue.map((option, index) => (
                    <Chip
                        label={`${option?.name}`}
                        {...getTagProps({ index })}
                        disabled={disabled || [...(optionDisabled || [])].indexOf(`${option?.code}`) !== -1}
                    />
                ))
            }
            renderInput={(params) => (
                <FormField
                    {...params}
                    size={size}
                    label={label}
                    variant={variant}
                    disabled={disabled}
                    required={required}
                    readOnly={readOnly}
                    value={valueSearchText}
                    errorMessage={errorMessage}
                    placeholder={getPlaceholder()}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {(open && loading) ? <CircularProgress color="info" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                    onChangeValue={(newValue) => {
                        deboundGetDataByApi(newValue);
                    }}
                />
            )}
            onChange={(e: any, selectedOption: any) => {
                handleOnChange(selectedOption);
            }}
        />
    );
};

export default Autocomplete;

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;

    const dataSet = data[index];

    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
    };

    if (dataSet.hasOwnProperty("group")) {
        return (
            dataSet.group
                ? <ListSubheader
                    component="div"
                    key={dataSet.key}
                    style={inlineStyle}
                    sx={theme => ({
                        fontWeight: 700,
                        color: "#637381",
                        position: "sticky",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        borderRadius: "8px",
                        backgroundColor: lighten(theme.palette.primary.light, 0.85),
                        ...theme.applyStyles('dark', {
                            backgroundColor: darken(theme.palette.primary.main, 0.8),
                        }),
                    })}
                >
                    {dataSet.group}
                </ListSubheader>
                : null
        );
    }

    return (
        <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
            {dataSet[1] === ADD_NEW_CODE && <AddNewItemRow />}
            {dataSet[1] === LOAD_MORE_CODE && <LoadMoreItemRow />}
            {(dataSet[1] !== ADD_NEW_CODE && dataSet[1] !== LOAD_MORE_CODE) && dataSet[1]}
        </Typography>
    );
}

const AddNewItemRow = () => {
    const strings = useStrings();
    return (
        <>
            <AddCircleOutline />
            &ensp;{strings.Common.ADD_NEW}...
        </>
    );
};

const LoadMoreItemRow = () => {
    const strings = useStrings();
    return (
        <>
            <RestartAltOutlined />
            &ensp;{strings.Common.LOAD_MORE}...
        </>
    );
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
    const ref = React.useRef<VariableSizeList>(null);
    React.useEffect(() => {
        if (ref?.current != null) {
            ref?.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;

    const itemData: React.ReactElement[] = [];

    (children as React.ReactElement[]).forEach((item: React.ReactElement & { children?: React.ReactElement[] }) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
    });

    const theme = useTheme();

    const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });

    const itemSize: number = smUp ? 36 : 48;

    const itemCount = itemData.length;

    const getChildSize = (child: React.ReactElement) => {
        if (child.hasOwnProperty("group")) {
            return (child as any)?.group ? 48 : 0;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    ref={gridRef}
                    width={"100%"}
                    overscanCount={5}
                    itemData={itemData}
                    itemCount={itemCount}
                    innerElementType={"ul"}
                    outerElementType={OuterElementType}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    itemSize={(index: number) => getChildSize(itemData[index])}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});
